/** @file Held items of a socket: unequip a mounted one, equip another (socket.equip / socket.unequip). */
import type { Doc, Id } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { useState } from 'react';
import { Button } from '../../ui-kit/Button';
import { Checkbox } from '../../ui-kit/Checkbox';
import { Select } from '../../ui-kit/Select';
import type { RunCommands } from '../rigKit/rigView';
import { itemsOfSocket } from './itemsOfSocket';

/** Props of SocketItems; `socket` is the selected socket's id. */
export interface SocketItemsProps {
  doc: Doc;
  t: Translate;
  run: RunCommands;
  socket: Id;
}

/** The equip choice falls back to the first equippable item whenever the remembered one is gone. */
export function SocketItems({ doc, t, run, socket }: SocketItemsProps) {
  const { mounted, equippable } = itemsOfSocket(doc, socket);
  const [choice, setChoice] = useState<string | null>(null);
  const [fitLength, setFitLength] = useState(false);
  const item = equippable.some((o) => o.value === choice) ? choice : (equippable[0]?.value ?? null);
  return (
    <fieldset className="rig-fields">
      <legend>{t('panels.sockets.items')}</legend>
      <ul aria-label={t('panels.sockets.items')}>
        {mounted.map((m) => (
          <li key={m.id} className="rig-row">
            {m.name}
            <Button label={t('panels.sockets.unequip')} onClick={() => { run([{ name: 'socket.unequip', input: { item: m.id } }]); }} />
          </li>
        ))}
      </ul>
      {item === null ? <p className="rig-hint">{t('panels.sockets.noItems')}</p> : (
        <div className="rig-row">
          <Select label={t('panels.sockets.item')} value={item} options={equippable} onChange={setChoice} />
          <Checkbox label={t('panels.sockets.fitLength')} checked={fitLength} onChange={setFitLength} />
          <Button label={t('panels.sockets.equip')} onClick={() => { run([{ name: 'socket.equip', input: { item, socket, fitLength } }]); }} />
        </div>
      )}
    </fieldset>
  );
}
